import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { SimpleTaskType, TaskFormatField } from 'models/task';
import { isTaskTypeSimple } from 'pages/task/lib/isTaskTypeSimple';
import { taskManagerApi } from 'services/rtk/task-manager';

import type {
  AnswerInfoForm,
  CurrentAnswer,
  SimpleTaskInfoForm,
  TaskManagerState,
  TasksFilters,
} from './types';

const initialState: TaskManagerState = {
  tasksFilters: {
    author: undefined,
  },
  taskInfo: {
    formFields: undefined,
    taskId: undefined,
    taskType: undefined,
    isInited: false,
    isChanged: false,
  },
  answerInfo: {
    formFields: undefined,
    isInited: false,
    isChanged: false,
  },
  currentAnswer: undefined,
  isCommentsDrawerOpen: false,
  isCreateAnswerLoading: false,
  isUpdateAnswerProcess: false,
  latestGetAnswersParams: undefined,
  latestUpdateAnswerRequestId: undefined,
  latestCreateCommentRequestId: undefined,
};

export const taskManagerSlice = createSlice({
  name: 'taskManager',
  initialState,
  reducers: {
    updateTasksFilters: (
      state,
      action: PayloadAction<{ filter: keyof TasksFilters; value: TasksFilters[keyof TasksFilters] }>
    ) => {
      const { filter, value } = action.payload;
      state.tasksFilters = {
        ...state.tasksFilters,
        [filter]: value,
      };
    },

    updateTaskInfoForm: (
      state,
      action: PayloadAction<{ type: SimpleTaskType; fields: Partial<SimpleTaskInfoForm> }>
    ) => {
      const { type, fields } = action.payload;
      if (state.taskInfo.isInited && !state.taskInfo.isChanged) {
        state.taskInfo.isChanged = true;
      }
      if (!state.taskInfo.isInited) {
        state.taskInfo.isInited = true;
      }
      if (isTaskTypeSimple(type)) {
        state.taskInfo.formFields = {
          ...(state.taskInfo.formFields as SimpleTaskInfoForm),
          ...fields,
        };
      }
    },
    updateTaskFormatField: (
      state,
      action: PayloadAction<{ id: TaskFormatField['id']; value: Partial<Omit<TaskFormatField, 'id'>> }>
    ) => {
      const { id, value } = action.payload;

      if (isTaskTypeSimple(state.taskInfo.taskType) && state.taskInfo.formFields) {
        const format = state.taskInfo.formFields.format;

        let isChanged = false;

        for (const entry of Object.entries(value)) {
          const [key, value] = entry;
          const storedValue = format.fields[id][key as keyof TaskFormatField];
          if (value !== storedValue) {
            isChanged = true;
          }
        }

        if (!isChanged) return;

        state.taskInfo.isChanged = isChanged;

        state.taskInfo.formFields.format.fields = {
          ...format.fields,
          [id]: {
            ...format.fields[id],
            ...value,
          },
        };

        if (value.field !== undefined) {
          const errors: string[] = [];

          if (value.field.trim() === '') {
            errors.push('Название поля не должно быть пустым');
          }

          const isDuplicate = Object.values(format.fields).some(
            (f) => f.field === value.field && f.id !== id
          );

          if (isDuplicate) {
            errors.push('Название поля должно быть уникальным');
          }

          state.taskInfo.formFields.format.errors = {
            ...format.errors,
            [id]: errors,
          };
        } else {
          const errors: string[] = [];

          const fieldValue = state.taskInfo.formFields.format.fields[id].field;

          if (fieldValue.trim() === '') {
            errors.push('Название поля не должно быть пустым');
          }

          state.taskInfo.formFields.format.errors = {
            ...format.errors,
            [id]: errors,
          };
        }
      }
    },
    updateTaskFormatFieldErrors: (
      state,
      action: PayloadAction<{ ids: TaskFormatField['id'][]; reason: string }>
    ) => {
      const { ids, reason } = action.payload;

      const taskInfo = state.taskInfo;

      if (isTaskTypeSimple(taskInfo.taskType) && taskInfo.formFields) {
        for (const id of ids) {
          const errors = taskInfo.formFields.format.errors[id];
          if (!errors) {
            taskInfo.formFields.format.errors[id] = [reason];
          } else if (!errors.includes(reason)) {
            errors.push(reason);
          }
        }
      }
    },
    addTaskFormatField: (state) => {
      const taskType = state.taskInfo.taskType;

      if (isTaskTypeSimple(taskType)) {
        let prevFormFields = state.taskInfo.formFields;

        if (!prevFormFields) {
          prevFormFields = {
            main: {
              assignees: [],
              name: '',
              description: '',
              status: 'not_started',
              type: taskType,
              viewAnswers: 'all',
            },
            format: {
              fields: {},
              order: [],
              errors: [],
            },
          };
        }

        const lastId = prevFormFields.format.order.reduce((acc, id) => {
          if (acc < id) acc = id;
          return acc;
        }, 0);
        const newId = lastId + 1;

        prevFormFields.format.fields[newId] = { id: newId, type: 'text', field: '', required: false };
        prevFormFields.format.order.push(newId);

        state.taskInfo.formFields = prevFormFields;
        state.taskInfo.isChanged = false;
      }
    },
    removeTaskFormatField: (state, action: PayloadAction<{ id: number }>) => {
      const { id } = action.payload;
      const taskType = state.taskInfo.taskType;

      if (taskType === 'ts' || taskType === 'reports') {
        const format = state.taskInfo.formFields?.format;

        if (format) {
          delete format.fields[id];
          format.order = format.order.filter((i) => i !== id);

          if (format.errors[id]) {
            delete format.errors[id];
          }

          for (const item of Object.values(format.fields)) {
            if (item.field === '' && !format.errors[item.id]) {
              format.errors[item.id] = ['Название поля не должно быть пустым'];
            }
          }
        }
      }
    },
    setTaskInfoFormChanged: (state, action: PayloadAction<boolean>) => {
      state.taskInfo.isChanged = action.payload;
    },
    setCurrentAnswer: (state, action: PayloadAction<CurrentAnswer>) => {
      if (!state.answerInfo) return;
      state.answerInfo.isChanged = false;
      state.currentAnswer = action.payload;
    },
    resetCurrentAnswer: (state) => {
      state.currentAnswer = initialState.currentAnswer;
    },
    setIsCommentsDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.isCommentsDrawerOpen = action.payload;
    },
    updateAnswerInfoForm: (
      state,
      action: PayloadAction<{
        format?: Partial<AnswerInfoForm['format']>;
        optional?: Partial<AnswerInfoForm['optional']>;
      }>
    ) => {
      const { format: formatFields, optional: optionalFields } = action.payload;

      if (!state.answerInfo) return;

      if (!state.answerInfo.isInited) {
        state.answerInfo.isInited = true;
      }

      let prevFields = state.answerInfo.formFields;

      state.answerInfo.formFields = {
        format: {
          fieldValues: { ...prevFields?.format.fieldValues, ...formatFields?.fieldValues },
          order: formatFields?.order ?? prevFields?.format.order ?? [],
        },
        optional: {
          text: optionalFields?.text ?? prevFields?.optional.text ?? '',
          files: optionalFields?.files ?? prevFields?.optional.files ?? [],
        },
      };
    },
    setIsAnswerInfoChanged: (state, action: PayloadAction<boolean>) => {
      if (!state.answerInfo) return;
      state.answerInfo.isChanged = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(taskManagerApi.endpoints.getTask.matchFulfilled, (state, action) => {
      if (!state.taskInfo.isInited) {
        state.taskInfo.isInited = true;
      }

      const { data: task } = action.payload;

      if (isTaskTypeSimple(task.type)) {
        const taskFields: SimpleTaskInfoForm = {
          main: {
            name: task.name,
            description: task.description,
            status: task.status,
            type: task.type,
            assignees: task.assignees,
            viewAnswers: task.viewAnswers,
          },
          format: { ...task.format, errors: {} },
        };

        state.taskInfo.formFields = taskFields;
      }

      state.taskInfo.taskId = task.id;
      state.taskInfo.taskType = task.type;
    });
    builder.addMatcher(taskManagerApi.endpoints.updateTask.matchFulfilled, (state) => {
      state.taskInfo.isChanged = false;
    });

    builder.addMatcher(taskManagerApi.endpoints.getAnswers.matchFulfilled, (state, action) => {
      const { originalArgs } = action.meta.arg;
      state.latestGetAnswersParams = originalArgs;
      if (state.isUpdateAnswerProcess) {
        state.isUpdateAnswerProcess = false;
      }
    });
    builder.addMatcher(taskManagerApi.endpoints.getAnswers.matchRejected, (state) => {
      if (state.isUpdateAnswerProcess) {
        state.isUpdateAnswerProcess = false;
      }
    });
    builder.addMatcher(taskManagerApi.endpoints.createAnswer.matchPending, (state) => {
      state.isCreateAnswerLoading = true;
    });
    builder.addMatcher(taskManagerApi.endpoints.createAnswer.matchFulfilled, (state) => {
      state.isCreateAnswerLoading = false;
    });
    builder.addMatcher(taskManagerApi.endpoints.createAnswer.matchRejected, (state) => {
      state.isCreateAnswerLoading = false;
    });
    builder.addMatcher(taskManagerApi.endpoints.updateAnswer.matchPending, (state, action) => {
      state.latestUpdateAnswerRequestId = action.meta.requestId;
      state.isUpdateAnswerProcess = true;
    });
    builder.addMatcher(taskManagerApi.endpoints.updateAnswer.matchFulfilled, (state, action) => {
      if (!state.answerInfo) return;
      state.answerInfo.isInited = false;
      state.answerInfo.isChanged = false;
    });
    builder.addMatcher(taskManagerApi.endpoints.createComment.matchPending, (state, action) => {
      state.latestCreateCommentRequestId = action.meta.requestId;
    });
  },
});

export const taskManagerReducer = taskManagerSlice.reducer;

export const {
  updateTasksFilters,

  updateTaskInfoForm,
  updateTaskFormatField,
  updateTaskFormatFieldErrors,
  addTaskFormatField,
  removeTaskFormatField,
  setTaskInfoFormChanged,
  setCurrentAnswer,
  resetCurrentAnswer,
  setIsCommentsDrawerOpen,
  updateAnswerInfoForm,
  setIsAnswerInfoChanged,
} = taskManagerSlice.actions;
